import { getArea } from 'ol/sphere';
import GeoJSON from 'ol/format/GeoJSON';
import { Geometry } from '@ais-ui/api-spec/shapes';

export interface AreaResult {
  /** Area value in km² */
  area: number;
  /** Unit string (always 'km²') */
  areaUnit: string;
  /** Formatted string like "123.45 km²" */
  formatted: string;
}

/**
 * Calculate the area of a polygon geometry.
 * Uses spherical calculations for accurate results.
 * 
 * @param geometry - GeoJSON geometry object (must be Polygon type)
 * @returns AreaResult or null if geometry is not a Polygon
 */
export const calculatePolygonArea = (geometry: Geometry): AreaResult | null => {
  if (geometry.type !== 'Polygon') {
    return null;
  }

  const format = new GeoJSON();
  const olGeometry = format.readGeometry(geometry);
  const areaInSquareMeters = getArea(olGeometry, { projection: 'EPSG:4326' });

  // Convert to km² and round to 2 decimal places
  const area = Math.round((areaInSquareMeters / 1_000_000) * 100) / 100;

  return {
    area,
    areaUnit: 'km²',
    formatted: `${area} km²`,
  };
};

/**
 * Calculate the area of a circle given its radius.
 * Uses the formula: A = π × r²
 * 
 * @param radiusKm - Radius in kilometers
 * @returns AreaResult
 */
export const calculateCircleArea = (radiusKm: number): AreaResult => {
  // Calculate area using π × r² and round to 2 decimal places
  const area = Math.round(Math.PI * Math.pow(radiusKm, 2) * 100) / 100;

  return {
    area,
    areaUnit: 'km²',
    formatted: `${area} km²`,
  };
};

/**
 * Calculate area for a shape based on its geometry type.
 * Supports Polygon and Circle (Point with radius) geometries.
 * 
 * @param geometry - GeoJSON geometry object
 * @param radius - Optional radius in km (for circle shapes)
 * @returns AreaResult or null if geometry type doesn't support area calculation
 */
export const calculateShapeArea = (
  geometry: Geometry | undefined,
  radius?: number
): AreaResult | null => {
  if (!geometry) {
    return null;
  }

  // Circle: Point geometry with radius
  if (geometry.type === 'Point' && radius !== undefined) {
    return calculateCircleArea(radius);
  }

  // Polygon
  if (geometry.type === 'Polygon') {
    return calculatePolygonArea(geometry);
  }

  // LineString, Point (without radius), etc. don't have area
  return null;
};
